import { Typography } from '@shared/ui';
import type { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const ADMIN_ROUTE_LABELS: Record<string, string> = {
  '/admin': 'Admin Home',
  '/admin/prompts': 'Prompt Editor',
  '/admin/profiles': 'Profile Editor',
  '/admin/users': 'User Management',
  '/admin/evals': 'Evals',
  '/admin/evals/runs': 'Runs',
  '/admin/internal-apps': 'Internal Apps',
};

const ADMIN_BREADCRUMB_TARGETS: Record<string, string> = {
  '/admin/evals/runs': '/admin/evals?tab=runs',
};

const AdminBreadcrumbs: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Only show breadcrumbs on admin routes
  if (!location.pathname.startsWith('/admin')) {
    return null;
  }

  // Build breadcrumb segments
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbs: Array<{ label: string; path: string }> = [];

  // Always start with Admin Home
  breadcrumbs.push({ label: 'Admin Home', path: '/admin' });

  // Build path incrementally and look up labels
  let currentPath = '/admin';
  for (let i = 1; i < pathSegments.length; i++) {
    currentPath += `/${pathSegments[i]}`;
    const label = ADMIN_ROUTE_LABELS[currentPath];
    const targetPath = ADMIN_BREADCRUMB_TARGETS[currentPath] ?? currentPath;
    if (label) {
      breadcrumbs.push({ label, path: targetPath });
    } else {
      // Fallback: format the segment name (e.g., "default" from slug)
      const formattedLabel = pathSegments[i]
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      breadcrumbs.push({ label: formattedLabel, path: targetPath });
    }
  }

  // Don't show breadcrumbs if we're just on /admin
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav aria-label="breadcrumb" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <div key={crumb.path} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isLast ? (
                <Typography variant="body1" color="text.primary">
                  {crumb.label}
                </Typography>
              ) : (
                <>
                  <Typography
                    component="button"
                    variant="body1"
                    onClick={() => navigate(crumb.path)}
                    sx={{
                      color: 'text.secondary',
                      textDecoration: 'none',
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {crumb.label}
                  </Typography>
                  <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>/</span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
};

export default AdminBreadcrumbs;
